import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

interface LoginProps {
  onLoginSuccess: () => void;
}

export default function Login({ onLoginSuccess }: LoginProps) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | null>(null);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      const msgs: Record<string, string> = {
        access_denied: '访问被拒绝，请重试。',
        invalid_request: '无效请求，请重试。',
        server_error: '服务器错误，请稍后重试。',
        oauth_failed: 'OAuth 认证失败，请重试。',
      };
      setError(msgs[oauthError] || `认证失败: ${oauthError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (step === 'code' && codeRef.current) codeRef.current.focus();
  }, [step]);

  // Detect iOS in-app browser (WeChat, QQ, etc.) and show Safari hint
  const [showSafariHint, setShowSafariHint] = useState(false);
  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
    const isInApp = /MicroMessenger|WeChat|QQ|MQQBrowser|Line|Instagram|FBAN|FBAV|Twitter/.test(ua);
    // Show hint if iOS and not Safari (in-app browser or Chrome etc.)
    if (isIOS && (!isSafari || isInApp)) {
      setShowSafariHint(true);
    }
  }, []);

  const handleOAuth = (provider: 'google') => {
    setOauthLoading(provider);
    setError('');
    window.location.href = `/api/auth/${provider}`;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('请输入有效的邮箱地址');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await api.login(email);
      if (result.success) {
        setStep('code');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '发送验证码失败');
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('请输入6位验证码');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await api.verifyCode(email, code);
      if (result.success) {
        onLoginSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证码错误');
    }
    setLoading(false);
  };

  const isAnyLoading = loading || oauthLoading !== null;

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.logo}>💕</div>
        <h1 style={styles.title}>AI 男友</h1>
        <p style={styles.subtitle}>你的专属 AI 伴侣</p>

        <div style={styles.card}>
          {error && <div style={styles.error}>{error}</div>}

          {step === 'email' ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                <button style={styles.oauthGoogle} onClick={() => handleOAuth('google')} disabled={isAnyLoading}>
                  {oauthLoading === 'google' ? <Spinner dark /> : <GoogleIcon />}
                  {oauthLoading === 'google' ? '连接中...' : '使用 Google 登录'}
                </button>
              </div>

              <div style={styles.divider}>
                <div style={styles.dividerLine} />
                <span style={{ color: '#666', fontSize: '0.85rem' }}>或</span>
                <div style={styles.dividerLine} />
              </div>

              <form onSubmit={handleSendCode} style={{ marginTop: 24 }}>
                <input
                  type="email"
                  placeholder="输入邮箱地址"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isAnyLoading}
                  required
                  style={styles.input}
                />
                <button type="submit" disabled={isAnyLoading} style={styles.btn}>
                  {loading ? <Spinner /> : null}
                  {loading ? '发送中...' : '获取验证码'}
                </button>
              </form>
            </>
          ) : (
            <>
              <p style={{ color: '#888', margin: '0 0 8px', fontSize: '0.95rem' }}>
                验证码已发送到
              </p>
              <p style={{ color: '#fff', margin: '0 0 24px', fontWeight: 600 }}>{email}</p>

              <form onSubmit={handleVerifyCode}>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 12 }}>
                {[0,1,2,3,4,5].map(i => (
                  <input
                    key={i}
                    id={`code-${i}`}
                    ref={i === 0 ? codeRef : undefined}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[i] || ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (v) {
                        const arr = code.split('');
                        arr[i] = v[0];
                        setCode(arr.join(''));
                        if (i < 5) document.getElementById(`code-${i+1}`)?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !code[i] && i > 0) {
                        const arr = code.split('');
                        arr[i-1] = '';
                        setCode(arr.join(''));
                        document.getElementById(`code-${i-1}`)?.focus();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                      setCode(paste);
                      const focusIdx = Math.min(paste.length, 5);
                      document.getElementById(`code-${focusIdx}`)?.focus();
                    }}
                    disabled={loading}
                    style={{
                      width: 44, height: 52, textAlign: 'center' as const,
                      fontSize: '1.5rem', fontWeight: 600,
                      borderRadius: 10,
                      border: code[i] ? '2px solid rgba(168,85,247,0.6)' : '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.08)', color: 'white',
                      outline: 'none', caretColor: '#a855f7',
                    }}
                  />
                ))}
              </div>
                <button type="submit" disabled={loading || code.length !== 6} style={styles.btn}>
                  {loading ? <Spinner /> : null}
                  {loading ? '验证中...' : '登录'}
                </button>
              </form>

              <button onClick={() => { setStep('email'); setCode(''); setError(''); }} style={styles.btnOutline}>
                换个邮箱
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner({ dark }: { dark?: boolean }) {
  return (
    <div style={{
      width: 20, height: 20,
      border: `2px solid ${dark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)'}`,
      borderTopColor: dark ? '#3c4043' : 'white',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', padding: 20,
    background: '#09090b', color: '#fafafa',
    fontFamily: "-apple-system, 'SF Pro Display', 'Inter', 'PingFang SC', sans-serif",
  },
  inner: { textAlign: 'center' as const, maxWidth: 400, width: '100%' },
  logo: { fontSize: '4rem', marginBottom: 16 },
  title: { fontSize: '2.5rem', margin: '0 0 8px', background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { color: '#888', fontSize: '1.1rem', margin: '0 0 40px' },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16, padding: 32,
  },
  input: {
    width: '100%', padding: '14px 16px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.1)', color: 'white',
    fontSize: '1rem', boxSizing: 'border-box' as const, marginBottom: 16,
    outline: 'none',
  },
  btn: {
    width: '100%', padding: "14px 24px", borderRadius: 12, border: "none",
    background: 'linear-gradient(135deg, #FF6B9D, #A855F7)',
    color: 'white', fontSize: '1rem', fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8,
  },
  btnOutline: {
    width: '100%', padding: 16, borderRadius: 8,
    background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
    color: 'white', fontSize: '1rem', fontWeight: 500, cursor: 'pointer',
    marginTop: 16,
  },
  oauthGoogle: {
    width: '100%', padding: '14px 16px', borderRadius: 8, border: '1px solid #dadce0',
    background: '#ffffff', color: '#3c4043', fontSize: '1rem', fontWeight: 500,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  },
  divider: { display: 'flex', alignItems: 'center', margin: '24px 0', gap: 16 },
  dividerLine: { flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' },
  error: {
    background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)',
    padding: 12, borderRadius: 8, marginBottom: 16, color: '#ef4444',
  },
};
